import React from "react";
import { Box, Typography, Paper, Grid, Button } from "@mui/material";
import {
  EventNote,
  Check,
  Info,
  Close,
  Delete,
  ArrowBack,
} from "@mui/icons-material";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import { formatDateRange } from "../../bookingCalendar/calendarUtils";
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  addDoc,
  collection,
} from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { db } from "../../../firebase";

const BookingDetailsView = ({
  bookingDetails,
  selectedDateStr,
  setBookingDetails,
  handleCloseDetails,
  setGlobalConfirmAction,
  setBookedDateRanges,
  setSnackbar,
}) => {
  // Format date for display - supports both single date and date range models
  const displayDateInfo = () => {
    if (bookingDetails.startDate && bookingDetails.endDate) {
      // For date range bookings
      return formatDateRange(
        bookingDetails.startDate.toDate
          ? bookingDetails.startDate.toDate()
          : bookingDetails.startDate,
        bookingDetails.endDate.toDate
          ? bookingDetails.endDate.toDate()
          : bookingDetails.endDate
      );
    } else if (bookingDetails.date) {
      // For legacy single date bookings
      return formatDateForDisplay(
        bookingDetails.date.toDate
          ? bookingDetails.date.toDate()
          : bookingDetails.date
      );
    }
    // Fallback to selected date string if no date info in booking
    return formatDateForDisplay(selectedDateStr);
  };

  // Toggle booking approval
  const toggleBookingApproval = async (bookingId, currentApprovalState) => {
    // First show confirmation modal
    setGlobalConfirmAction({
      title: currentApprovalState
        ? "Annuler l'approbation de la réservation"
        : "Approuver la réservation",
      message: currentApprovalState
        ? "Êtes-vous sûr de vouloir annuler l'approbation de cette réservation ? Le client sera notifié."
        : "Êtes-vous sûr de vouloir approuver cette réservation ? Le client sera notifié.",
      action: async () => {
        try {
          const bookingRef = doc(db, "bus_bookings", bookingId);
          await setDoc(
            bookingRef,
            { isApproved: !currentApprovalState },
            { merge: true }
          );

          // Update local state for the current booking details
          setBookingDetails((prev) => ({
            ...prev,
            isApproved: !currentApprovalState,
          }));

          // Update the booking in the bookedDateRanges array to reflect the change in parent component
          setBookedDateRanges((prev) =>
            prev.map((booking) => {
              if (booking.id === bookingId) {
                return {
                  ...booking,
                  bookingData: {
                    ...booking.bookingData,
                    isApproved: !currentApprovalState,
                  },
                };
              }
              return booking;
            })
          );

          setSnackbar({
            open: true,
            message: !currentApprovalState
              ? "Réservation approuvée avec succès"
              : "Approbation de réservation annulée",
            severity: "success",
          });
        } catch (error) {
          console.error("Error updating booking approval:", error);
          setSnackbar({
            open: true,
            message: "Erreur lors de la mise à jour de l'approbation",
            severity: "error",
          });
        }
      },
      type: currentApprovalState ? "disapprove" : "approve",
    });
  };

  // Delete booking and archive it - moved here from parent
  const handleDeleteBooking = (bookingId) => {
    setGlobalConfirmAction({
      title: "Annuler la réservation",
      message:
        "Êtes-vous sûr de vouloir annuler cette réservation ? Veuillez ajouter une note d'annulation. Le client sera notifié.",
      action: async (cancellationNote) => {
        try {
          // Validate the note
          if (!cancellationNote || cancellationNote.length === 0) {
            setSnackbar({
              open: true,
              message: "Veuillez ajouter une note d'annulation",
              severity: "error",
            });
            return;
          }

          // Get booking data before deletion
          const bookingRef = doc(db, "bus_bookings", bookingId);
          const bookingSnap = await getDoc(bookingRef);

          if (bookingSnap.exists()) {
            const bookingData = bookingSnap.data();

            // Add to archive with note
            await addDoc(collection(db, "bus_archive"), {
              ...bookingData,
              originalId: bookingId,
              cancelledAt: Timestamp.now(),
              cancellationNote: cancellationNote,
            });

            // Delete from bookings
            await deleteDoc(bookingRef);

            // Update UI state - remove the booking from the list
            setBookedDateRanges((prev) =>
              prev.filter((range) => range.id !== bookingId)
            );

            setSnackbar({
              open: true,
              message: "Réservation annulée et archivée avec succès",
              severity: "success",
            });

            // Close details
            handleCloseDetails();
          }
        } catch (error) {
          console.error("Error deleting booking:", error);
          setSnackbar({
            open: true,
            message: "Erreur lors de l'annulation de la réservation",
            severity: "error",
          });
        }
      },
      type: "delete",
    });
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Paper elevation={2} className="booking-details-paper">
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            color: "#4CAF50",
            display: "flex",
            alignItems: "center",
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          <EventNote sx={{ mr: 1 }} /> Détails de la réservation
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{
                fontSize: {
                  xs: "0.7rem",
                  sm: "0.8rem",
                },
              }}
            >
              Nom
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: { xs: 1, sm: 2 },
                fontWeight: 500,
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              {bookingDetails.name}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{
                fontSize: {
                  xs: "0.7rem",
                  sm: "0.8rem",
                },
              }}
            >
              {bookingDetails.startDate && bookingDetails.endDate
                ? "Période de réservation"
                : "Date de réservation"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: { xs: 1, sm: 2 },
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              {displayDateInfo()}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{
                fontSize: {
                  xs: "0.7rem",
                  sm: "0.8rem",
                },
              }}
            >
              Email
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: { xs: 1, sm: 2 },
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              {bookingDetails.email}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{
                fontSize: {
                  xs: "0.7rem",
                  sm: "0.8rem",
                },
              }}
            >
              Téléphone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: { xs: 1, sm: 2 },
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              {bookingDetails.phone}
            </Typography>
          </Grid>

          {/* Approval status */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "8px",
                bgcolor: bookingDetails.isApproved
                  ? "rgba(76, 175, 80, 0.1)"
                  : "rgba(255, 152, 0, 0.1)",
                border: bookingDetails.isApproved
                  ? "1px solid rgba(76, 175, 80, 0.3)"
                  : "1px solid rgba(255, 152, 0, 0.3)",
                mb: 3,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: bookingDetails.isApproved ? "#388e3c" : "#f57c00",
                  fontWeight: 500,
                }}
              >
                {bookingDetails.isApproved ? (
                  <Check sx={{ mr: 1, fontSize: "1.2rem" }} />
                ) : (
                  <Info sx={{ mr: 1, fontSize: "1.2rem" }} />
                )}
                {bookingDetails.isApproved
                  ? "Réservation approuvée"
                  : "En attente d'approbation"}
              </Typography>
            </Paper>
          </Grid>

          {bookingDetails.notes && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Notes
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 0.5, sm: 1, md: 3 },
                  borderRadius: "5px",
                  bgcolor: "#f9f9f9",
                }}
              >
                <Typography variant="body2">{bookingDetails.notes}</Typography>
              </Paper>
            </Grid>
          )}

          {bookingDetails.createdAt && (
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 2, display: "block" }}
              >
                Réservation faite le{" "}
                {new Date(bookingDetails.createdAt.toDate()).toLocaleString(
                  "fr-FR"
                )}
              </Typography>
            </Grid>
          )}

          {/* Action buttons for booking management */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                gap: 2,
                mt: 3,
              }}
            >
              {/* Approve/Disapprove button */}
              <Button
                variant="contained"
                color={bookingDetails.isApproved ? "warning" : "success"}
                startIcon={bookingDetails.isApproved ? <Close /> : <Check />}
                onClick={() =>
                  toggleBookingApproval(
                    bookingDetails.id,
                    bookingDetails.isApproved
                  )
                }
                fullWidth
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  py: 1.5,
                }}
              >
                {bookingDetails.isApproved
                  ? "Annuler l'approbation"
                  : "Approuver la réservation"}
              </Button>

              {/* Delete booking button */}
              <Button
                variant="contained"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleDeleteBooking(bookingDetails.id)}
                fullWidth
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  py: 1.5,
                }}
              >
                Annuler la réservation
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Button
        variant="contained"
        className="back-button admin-action-button"
        onClick={handleCloseDetails}
        sx={{ mt: 2 }}
        startIcon={<ArrowBack />}
      >
        Retour
      </Button>
    </Box>
  );
};

export default BookingDetailsView;
